import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Building2, ShieldCheck, X, Link2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Surfaced inside athlete Settings: shows the athlete who currently represents
 * them (if anyone) and gives a one-click revoke. Identity stays portable —
 * the athlete is the primary entity, the agency just represents them.
 */
export default function RepresentationCard() {
  const [reps, setReps] = useState(null);

  const load = () => {
    api.get("/athlete/representation").then(({ data }) => setReps(data))
      .catch(() => setReps([]));
  };
  useEffect(() => { load(); }, []);

  const revoke = async (id, orgName) => {
    if (!window.confirm(`Revoke commercial representation by ${orgName}? They'll lose access to your roster immediately. Your sponsorship history stays with you.`)) return;
    try {
      await api.post(`/athlete/representation/${id}/revoke`);
      toast.success("Representation revoked");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!reps) return null;

  return (
    <section className="surface p-6 mt-6" data-testid="representation-card">
      <div className="label-eyebrow flex items-center gap-2"><Link2 className="w-3 h-3" /> Representation</div>
      <h3 className="font-heading text-xl uppercase tracking-tight mt-1">Who represents you</h3>
      <p className="text-xs text-ink-muted mt-2">
        Your identity is portable. Any agency below works on your behalf — you can revoke representation at any time. Your sponsorship history, media kit and partnerships stay with you.
      </p>
      {reps.length === 0 ? (
        <div className="mt-5 text-sm text-ink-muted italic">You're self-managed — no agency currently represents you.</div>
      ) : (
        <div className="mt-5 divide-y divide-line">
          {reps.map(r => (
            <div key={r.id} className="py-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`representation-${r.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-bg-elev border border-line flex items-center justify-center shrink-0">
                  {r.organisation?.organisation_logo ? <img src={r.organisation.organisation_logo} alt="" className="w-full h-full object-cover" /> : <Building2 className="w-5 h-5 text-volt" />}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate" data-testid={`representation-name-${r.id}`}>{r.organisation?.organisation_name || "Independent representative"}</div>
                  <div className="text-[10px] uppercase tracking-widest text-ink-muted mt-0.5 flex items-center gap-2">
                    {r.relationship_type?.replace("_", " ")} · since {r.started_at?.slice(0, 10)}
                    {r.organisation?.verification_status === "platform_verified" && (
                      <span className="text-volt inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Verified</span>
                    )}
                  </div>
                  {r.representative?.full_name && (
                    <div className="text-xs text-ink-muted mt-1">Your contact: {r.representative.full_name}</div>
                  )}
                </div>
              </div>
              <button onClick={() => revoke(r.id, r.organisation?.organisation_name || "this agency")}
                data-testid={`revoke-representation-${r.id}`}
                className="text-xs uppercase tracking-widest border border-[#FF3B30]/40 text-[#FF3B30] px-3 py-2 hover:bg-[#FF3B30]/10 inline-flex items-center gap-1">
                <X className="w-3 h-3" /> Revoke
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
